import { TransportProvider } from '@connectrpc/connect-query'
import { CoralogixRum } from '@coralogix/browser'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { LicenseManager } from 'ag-grid-enterprise'
import { ConfigProvider } from 'antd'
import type { Config as MixpanelConfig } from 'mixpanel-browser'
import mixpanel from 'mixpanel-browser'
import { PostHogProvider } from 'posthog-js/react'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'

import { antdTheme } from '@/const/theme'

import { installFlagDebugWithDefaults } from '@/lib/analytics/posthog-flag-debug'
import { installFlagMonitoringWithDefaults } from '@/lib/analytics/posthog-flag-monitoring'
import type { BuildMode } from '@/lib/config/coralogix.config'
import { coralogixOptions } from '@/lib/config/coralogix.config'
import { retryLogic } from '@/lib/connect/connect-errors'
import { defaultTransport } from '@/lib/connect/connect-transport'

import { Toaster } from '@/components/lemmala-design-system/toast/toaster'
import { reportStaleChunkReload } from '@/components/shared/error-boundary/utils'

import { App } from './App'

import './index.css'

const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY
const posthogHost = import.meta.env.VITE_POSTHOG_HOST
const posthogUiHost = import.meta.env.VITE_POSTHOG_UI_HOST
const buildMode = import.meta.env.MODE
if (!['development', 'preview', 'staging', 'production', 'production_us'].includes(buildMode)) {
  console.warn(`Unknown build mode: ${buildMode}`)
}
// Initialize AG Grid Enterprise license (provided via Vite env)
LicenseManager.setLicenseKey(import.meta.env.VITE_AG_GRID_LICENSE_KEY)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: retryLogic,
      staleTime: 1000 * 60, // 1 minute
      refetchOnWindowFocus: true,
      gcTime: 1000 * 60 * 5,
    },
  },
})

const isProductionBuild = buildMode === 'production' || buildMode === 'production_us'

// Define mixpanel configuration with partial type
const mixpanelOpts: Partial<MixpanelConfig> = {
  record_sessions_percent: isProductionBuild ? 100 : 0,
  ignore_dnt: true,
  record_mask_text_selector: '',
  track_pageview: true,
  record_inline_images: true,
  record_heatmap_data: true,
}

if (buildMode === 'staging' || isProductionBuild) {
  mixpanelOpts.api_host = window.location.origin + '/v4.analytics'
} else {
  mixpanelOpts.api_host = 'https://api-eu.mixpanel.com'
}
mixpanel.init(import.meta.env.VITE_MIXPANEL_TOKEN, mixpanelOpts)
mixpanel.register({
  env: import.meta.env.MODE,
  'Screen Resolution': `${window.screen.width}x${window.screen.height}`,
})

const coralogixOpts = coralogixOptions(buildMode as BuildMode)
CoralogixRum.init(coralogixOpts)
reportStaleChunkReload()

ReactDOM.createRoot(document.getElementById('root')!).render(
  <BrowserRouter>
    <React.StrictMode>
      <ConfigProvider theme={antdTheme}>
        <PostHogProvider
          apiKey={posthogKey}
          options={{
            api_host: posthogHost,
            ui_host: posthogUiHost,
            disable_session_recording: true,
            disable_product_tours: true,
            remote_config_refresh_interval_ms: 300_000,
            loaded: (posthog) => {
              installFlagDebugWithDefaults(posthog)
              installFlagMonitoringWithDefaults(posthog)
            },
          }}
        >
          <TransportProvider transport={defaultTransport}>
            <QueryClientProvider client={queryClient}>
              <App />
              <Toaster />
              {buildMode == 'development' && <ReactQueryDevtools position='left' />}
            </QueryClientProvider>
          </TransportProvider>
        </PostHogProvider>
      </ConfigProvider>
    </React.StrictMode>
  </BrowserRouter>,
)
